// Formulário único de criar e editar contato (modal).
import React, { useState } from 'react';
import Modal from 'react-modal';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { addDoc, collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { firestore } from '../../config/firebaseConfig';
import { variantesTelefone } from '../relatorios/util';
import { STATUS_CONTATO, mascaraTelefone, telefoneParaEditar, telefoneParaSalvar } from './contatosUtil';

const GENEROS = ['', 'Feminino', 'Masculino', 'Outro', 'Prefiro não informar'];
const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formularioInicial = (contato) => ({
  nome: contato?.nome || '',
  telefone: mascaraTelefone(telefoneParaEditar(contato?.telefone)),
  email: contato?.email || '',
  dataNascimento: contato?.dataNascimento || '',
  genero: contato?.genero || '',
  tipoContato: contato?.tipoContato || 'Lead',
  status: contato?.status || 'Pendente',
  interesses: contato?.interesses || '',
  linkedin: contato?.linkedin || '',
  curso: contato?.curso || '',
  turma: contato?.turma || '',
});

function ContatoFormModal({ tenantId, contato, onFechar, onSalvo }) {
  const editando = Boolean(contato?.id);
  const [form, setForm] = useState(() => formularioInicial(contato));
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);

  const alterar = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === 'telefone' ? mascaraTelefone(value) : value }));
    setErros((x) => ({ ...x, [name]: undefined }));
  };

  // Duplicados só dentro da empresa; telefone comparado com e sem o 9
  const duplicados = async (telefone, email) => {
    const s = await getDocs(query(collection(firestore, 'contatos'), where('tenantId', '==', tenantId)));
    const variantes = variantesTelefone(telefone);
    const problemas = {};
    s.forEach((d) => {
      if (d.id === contato?.id) return;
      const c = d.data();
      if (variantes.some((v) => variantesTelefone(c.telefone).includes(v))) problemas.telefone = `Já existe um contato com este telefone (${c.nome || 'sem nome'}).`;
      if (email && c.email && c.email.toLowerCase() === email.toLowerCase()) problemas.email = `Já existe um contato com este e-mail (${c.nome || 'sem nome'}).`;
    });
    return problemas;
  };

  const salvar = async (e) => {
    e.preventDefault();
    const digitos = form.telefone.replace(/\D/g, '');
    const email = form.email.trim();
    const novosErros = {};
    if (!form.nome.trim()) novosErros.nome = 'Informe o nome.';
    if (digitos.length < 10) novosErros.telefone = 'Informe DDD + número (10 ou 11 dígitos).';
    if (email && !EMAIL_VALIDO.test(email)) novosErros.email = 'E-mail inválido.';
    if (Object.keys(novosErros).length) { setErros(novosErros); return; }

    setSalvando(true);
    try {
      const telefone = telefoneParaSalvar(digitos);
      const conflitos = await duplicados(telefone, email);
      if (Object.keys(conflitos).length) { setErros(conflitos); return; }

      const agora = new Date().toISOString();
      const dados = {
        nome: form.nome.trim(), telefone, email, dataNascimento: form.dataNascimento, genero: form.genero,
        tipoContato: form.tipoContato, status: form.status, interesses: form.interesses.trim(),
        linkedin: form.linkedin.trim(), curso: form.curso.trim(), turma: form.turma.trim(),
        dataUltimaAtualizacao: agora,
      };
      if (editando) {
        // Telefone novo: o vínculo com o WhatsApp é refeito na próxima matrícula
        if (telefone !== contato.telefone) dados.whatsappId = null;
        await updateDoc(doc(firestore, 'contatos', contato.id), dados);
        toast.success('Contato atualizado.');
      } else {
        await addDoc(collection(firestore, 'contatos'), { ...dados, tenantId, origem: 'Manual', dataCriacao: agora });
        toast.success('Contato cadastrado.');
      }
      onSalvo?.();
      onFechar();
    } catch (erro) {
      console.error('Erro ao salvar contato:', erro);
      toast.error('Não foi possível salvar o contato. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const campo = (nome, rotulo, props = {}) => (
    <div className={`ct-campo${props.largo ? ' ct-campo--largo' : ''}`}>
      <label htmlFor={`ct-${nome}`}>{rotulo}</label>
      <input
        id={`ct-${nome}`}
        name={nome}
        value={form[nome]}
        onChange={alterar}
        aria-invalid={Boolean(erros[nome])}
        aria-describedby={erros[nome] ? `ct-${nome}-erro` : undefined}
        {...props.input}
      />
      {erros[nome] && <span id={`ct-${nome}-erro`} className="ct-campo__erro">{erros[nome]}</span>}
    </div>
  );

  return (
    <Modal
      isOpen
      onRequestClose={onFechar}
      className="ct-modal"
      overlayClassName="ct-overlay"
      contentLabel={editando ? 'Editar contato' : 'Novo contato'}
    >
      <form onSubmit={salvar} noValidate>
        <header className="ct-modal__topo">
          <h2>{editando ? 'Editar contato' : 'Novo contato'}</h2>
          <button type="button" className="btn-ghost ct-icone-botao" onClick={onFechar} aria-label="Fechar">
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div className="ct-grade">
          {campo('nome', 'Nome *', { largo: true, input: { autoFocus: true, autoComplete: 'off' } })}
          {campo('telefone', 'Telefone (WhatsApp) *', { input: { type: 'tel', inputMode: 'numeric', placeholder: '(00) 00000-0000' } })}
          {campo('email', 'E-mail', { input: { type: 'email' } })}
          {campo('dataNascimento', 'Data de nascimento', { input: { type: 'date' } })}
          <div className="ct-campo">
            <label htmlFor="ct-genero">Gênero</label>
            <select id="ct-genero" name="genero" value={form.genero} onChange={alterar}>
              {GENEROS.map((g) => <option key={g} value={g}>{g || 'Não informado'}</option>)}
            </select>
          </div>
          <div className="ct-campo">
            <label htmlFor="ct-tipoContato">Tipo</label>
            <select id="ct-tipoContato" name="tipoContato" value={form.tipoContato} onChange={alterar}>
              <option value="Lead">Lead</option>
              <option value="Aluno">Aluno</option>
            </select>
          </div>
          <div className="ct-campo">
            <label htmlFor="ct-status">Status</label>
            <select id="ct-status" name="status" value={form.status} onChange={alterar}>
              {STATUS_CONTATO.map((s) => <option key={s.valor} value={s.valor}>{s.rotulo} · {s.descricao}</option>)}
            </select>
          </div>
          {campo('interesses', 'Interesses', { largo: true })}
          {campo('linkedin', 'LinkedIn', { largo: true, input: { type: 'url', placeholder: 'https://' } })}
        </div>

        <details className="ct-mais">
          <summary>Mais informações</summary>
          <div className="ct-grade">
            {campo('curso', 'Curso de origem (faculdade/escola)')}
            {campo('turma', 'Turma')}
          </div>
        </details>

        <footer className="ct-modal__rodape">
          <button type="button" className="btn-secondary ct-botao" onClick={onFechar}>Cancelar</button>
          <button type="submit" className="btn-primary ct-botao" disabled={salvando}>
            {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Cadastrar contato'}
          </button>
        </footer>
      </form>
    </Modal>
  );
}

export default ContatoFormModal;
